import { create } from 'zustand';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { toast } from 'react-toastify';
import { errorHandler } from '@/utils/functions/errorHandler';

export interface IReward {
  id?: number | string;
  tenant_id?: number | string;
  org_chart_node_id?: number;
  org_chart_node?: {
    id: number;
    name: string;
  };
  name?: string;
  value?: string;
  description?: string;
  bonus_name?: string;
  bonus_period?: string;
  bonus_target?: string;
  bonus_amount?: string;
  is_published?: boolean;
  attachment_file?: any;
  created_at?: string;
  updated_at?: string;
  fileUrl?: string;
  category?: string;
}

interface PaginationParams {
  page?: number;
  page_size?: number;
  order?: 'asc' | 'desc';
  order_by?: string;
  org_chart_node_id?: number;
  published?: boolean;
  search?: string;
}

interface PaginatedData {
  content: IReward[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

interface RewardState {
  rewards: IReward[];
  paginatedData: PaginatedData | null;
  selectedReward: IReward | null;
  isLoading: boolean;
  error: unknown;

  createReward: (tenantId: string | number, payload: Partial<IReward>) => Promise<void>;
  getRewards: (tenantId: string | number, params?: PaginationParams) => Promise<void>;
  getRewardDetail: (tenantId: string | number, id: string | number) => Promise<IReward | void>;
  updateReward: (tenantId: string | number, id: string | number, payload: Partial<IReward>) => Promise<void>;
  deleteReward: (tenantId: string | number, id: string | number) => Promise<void>;
  clearRewards: () => void;
  setRewards: (rewards: IReward[]) => void;
  bulkPublishReward: (tenantId: string | number, isPublished: boolean) => Promise<void>;
}

const normalizeReward = (reward: IReward | null | undefined): IReward => {
  // Defensive: if the API returns an empty body for a successful write,
  // upstream callers should not crash with TypeError. Returning an empty
  // shell keeps the store in a sane state until the next refetch.
  if (!reward) return {} as IReward;

  let fileUrl: string | null = null;

  if (reward.attachment_file) {
    try {
      const files = JSON.parse(reward.attachment_file);
      if (files.length > 0 && files[0].url) {
        fileUrl = files[0].url;
      }
    } catch {
      fileUrl = null;
    }
  }

  const rawBonusTarget = reward.bonus_target as unknown;
  const bonusTargetStr =
    typeof rawBonusTarget === 'string'
      ? rawBonusTarget
      : typeof rawBonusTarget === 'number'
      ? String(rawBonusTarget)
      : typeof reward.value === 'string'
      ? reward.value
      : '';

  const rawBonusAmount = reward.bonus_amount as unknown;
  const bonusAmountStr =
    typeof rawBonusAmount === 'string'
      ? rawBonusAmount
      : typeof rawBonusAmount === 'number'
      ? String(rawBonusAmount)
      : '';

  return {
    ...reward,
    bonus_name: reward.bonus_name ?? reward.name ?? '',
    bonus_target: bonusTargetStr,
    bonus_period: reward.bonus_period ?? '',
    bonus_amount: bonusAmountStr,
    fileUrl: fileUrl ?? undefined,
    category: reward.category ?? '',
  };
};

const useRewardState = create<RewardState>((set, get) => ({
  rewards: [],
  paginatedData: null,
  selectedReward: null,
  isLoading: false,
  error: null,

  setRewards: rewards => set({ rewards }),
  clearRewards: () => set({ rewards: [] }),

  createReward: async (tenantId, payload) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/rewards/${tenantId}`;
      const requestPayload = {
        bonus_name: payload.bonus_name,
        bonus_period: payload.bonus_period,
        bonus_target: payload.bonus_target,
        bonus_amount: payload.bonus_amount,
        is_published: payload.is_published ?? false,
        bonus_category: payload.category ?? '',
        org_chart_node_id: payload.org_chart_node_id ?? null,
      };

      await axiosInstance.post(url, requestPayload);

      await get().getRewards(tenantId);
      toast.success('Reward berhasil dibuat');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  updateReward: async (tenantId, id, payload) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/rewards/${tenantId}/${id}`;
      const requestPayload = {
        bonus_name: payload.bonus_name,
        bonus_period: payload.bonus_period,
        bonus_target: payload.bonus_target,
        bonus_amount: payload.bonus_amount,
        is_published: payload.is_published ?? false,
        bonus_category: payload.category ?? '',
        org_chart_node_id: payload.org_chart_node_id ?? null,
      };

      const { data } = await axiosInstance.put(url, requestPayload);

      // BE returns the updated reward as the top-level JSON body (not
      // wrapped in `{ data: ... }`), so prefer `data.data` when present
      // but fall back to `data` itself. Without this fallback,
      // `normalizeReward(undefined)` throws and the catch arm fires an
      // error toast even though the PUT succeeded.
      const updatedReward = (data?.data ?? data) as IReward;

      set(state => ({
        rewards: state.rewards.map(rw => (rw.id === id ? normalizeReward({ ...rw, ...updatedReward }) : rw)),
        selectedReward: normalizeReward(updatedReward),
      }));

      toast.success('Reward berhasil diperbarui');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  getRewards: async (tenantId, params) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/rewards/${tenantId}`;
      const { data } = await axiosInstance.get(url, { params });

      const rewardsData = (data.data?.content || data.data || []).map((reward: IReward) => normalizeReward(reward));

      // Set both rewards (for backward compatibility) and paginatedData
      set({
        rewards: rewardsData,
        paginatedData: {
          content: rewardsData,
          totalElements: data.data?.totalElements || rewardsData.length,
          totalPages: data.data?.totalPages || 1,
          size: data.data?.size || params?.page_size || 10,
          number: data.data?.number || params?.page || 0,
        },
      });
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  getRewardDetail: async (tenantId, id) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/rewards/${tenantId}/${id}`;
      const { data } = await axiosInstance.get(url);
      const normalizedData = normalizeReward(data.data);
      set({ selectedReward: normalizedData });
      return normalizedData;
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  deleteReward: async (tenantId, id) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/rewards/${tenantId}/${id}`;
      await axiosInstance.delete(url);
      set(state => ({
        rewards: state.rewards.filter(rw => rw.id !== id),
      }));
      toast.success('Reward berhasil dihapus');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  bulkPublishReward: async (tenantId: string | number, isPublished: boolean) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/rewards/${tenantId}/bulk-publish`;
      await axiosInstance.put(url, { published: isPublished });

      set(state => ({
        rewards: state.rewards.map(ka => ({
          ...ka,
          is_published: isPublished,
        })),
        isLoading: false,
      }));

      toast.success(
        isPublished ? 'Semua reward berhasil dipublikasikan' : 'Semua reward berhasil dibatalkan publikasinya',
      );
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },
}));

export default useRewardState;
